import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Phone, Mail, MapPin, Car, Hammer, FileText, Trash2, Send, Loader2, Pencil, Save, X, ShieldCheck, StickyNote, Building2 } from "lucide-react";
import { api, money, downloadPdf } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState } from "@/components/States";
import { StatusChip } from "@/components/StatusChip";
import { NumberPlate } from "@/components/NumberPlate";
import { RelatedExpenses } from "@/components/RelatedExpenses";
import { WhatsAppSendDialog, WhatsAppButton } from "@/components/WhatsAppSendDialog";
import { LogCallModal } from "@/components/LogCallModal";
import { WhatsAppLog } from "@/components/WhatsAppLog";
import { waCustomerText } from "@/lib/whatsapp";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waRefresh, setWaRefresh] = useState(0);
  const [callOpen, setCallOpen] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try { setData(await api(`/customers/${id}/summary`)); } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const startEdit = () => {
    const c = data.customer;
    setForm({ name: c.name || "", phone: c.phone || "", email: c.email || "", address: c.address || "", notes: c.notes || "",
      customer_type: c.customer_type || "individual", company_name: c.company_name || "", vat_registered: !!c.vat_registered, vat_number: c.vat_number || "" });
    setEditing(true);
  };
  const saveEdit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api(`/customers/${id}`, { method: "PATCH", body: JSON.stringify({
        name: form.name.trim(), phone: form.phone.trim() || null, email: form.email.trim() || null,
        address: form.address.trim() || null, notes: form.notes.trim() || null,
        customer_type: form.customer_type,
        company_name: form.customer_type === "company" ? (form.company_name.trim() || null) : null,
        vat_registered: !!form.vat_registered,
        vat_number: form.vat_registered ? (form.vat_number.trim() || null) : null,
      }) });
      toast.success("Customer updated"); setEditing(false); load();
    } catch (e) { toast.error(e.message || "Could not update customer"); } finally { setSaving(false); }
  };

  const statement = async () => {
    try { await downloadPdf(`/customers/${id}/statement.pdf`, `statement-${id}.pdf`); }
    catch (e) { toast.error(e.message || "Could not generate statement"); }
  };
  const gdprPdf = async () => {
    try { await downloadPdf(`/customers/${id}/gdpr.pdf`, `gdpr-export-${id}.pdf`); toast.success("GDPR data export downloaded"); }
    catch (e) { toast.error(e.message || "Could not generate export"); }
  };
  const emailStatement = async () => {
    setEmailing(true);
    try {
      const res = await api(`/customers/${id}/statement/email`, { method: "POST" });
      if (res.email_delivered) toast.success(`Statement emailed to ${res.to}`);
      else if (!res.email_configured) toast.success(`Statement prepared for ${res.to} (email sending not configured in preview — works once deployed)`);
      else toast.success(`Statement sent to ${res.to}`);
    } catch (e) { toast.error(e.message || "Could not email statement"); } finally { setEmailing(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this customer? This cannot be undone.")) return;
    try { await api(`/customers/${id}`, { method: "DELETE" }); toast.success("Customer deleted"); navigate("/customers"); }
    catch (e) { toast.error(e.message); }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const { customer, vehicles, jobs, invoices, totals } = data;

  return (
    <div>
      <Link to="/customers" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Customers</Link>
      <PageHeader title={customer.name}
        actions={<>
          {!editing && <button onClick={startEdit} data-testid="edit-customer-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-midnight hover:bg-secondary"><Pencil className="h-4 w-4" />Edit</button>}
          <button onClick={emailStatement} disabled={emailing || !customer.email} data-testid="email-statement-btn" title={customer.email ? `Email statement to ${customer.email}` : "No email address on file"} className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-midnight hover:bg-secondary disabled:opacity-50">{emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Email Statement</button>
          <WhatsAppButton onClick={() => setWaOpen(true)} testId="customer-whatsapp-btn" />
          <button onClick={() => setCallOpen(true)} data-testid="log-call-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-midnight hover:bg-secondary"><Phone className="h-4 w-4" />Log Call</button>
          <button onClick={statement} data-testid="statement-pdf-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-midnight hover:bg-secondary"><Download className="h-4 w-4" />Statement PDF</button>
          <button onClick={gdprPdf} data-testid="gdpr-pdf-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-midnight hover:bg-secondary"><ShieldCheck className="h-4 w-4" />GDPR Data (PDF)</button>
          <button onClick={remove} data-testid="delete-customer-btn" className="inline-flex items-center gap-2 rounded-lg border border-danger/30 bg-danger-bg px-3 py-2.5 text-sm font-bold text-danger"><Trash2 className="h-4 w-4" /></button>
        </>} />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5 lg:col-span-1">
          {editing ? (
            <>
              <h3 className="mb-3 font-display text-lg font-bold uppercase text-midnight">Edit Customer</h3>
              <div className="space-y-3">
                <EF label="NAME *" v={form.name} on={(x) => setForm({ ...form, name: x })} t="edit-cust-name" />
                <EF label="PHONE" v={form.phone} on={(x) => setForm({ ...form, phone: x })} t="edit-cust-phone" />
                <EF label="EMAIL" v={form.email} on={(x) => setForm({ ...form, email: x })} t="edit-cust-email" />
                <EF label="ADDRESS" v={form.address} on={(x) => setForm({ ...form, address: x })} t="edit-cust-address" />
                <div>
                  <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">CUSTOMER TYPE</span>
                  <div className="flex gap-2">
                    {["individual", "company"].map((t) => (
                      <button key={t} type="button" onClick={() => setForm({ ...form, customer_type: t })} data-testid={`edit-cust-type-${t}`}
                        className={`flex-1 rounded-lg border px-3 py-2 text-sm font-bold capitalize ${form.customer_type === t ? "border-brand bg-brand-tertiary text-brand" : "border-border bg-card text-midnight"}`}>{t}</button>
                    ))}
                  </div>
                </div>
                {form.customer_type === "company" && <EF label="COMPANY NAME" v={form.company_name} on={(x) => setForm({ ...form, company_name: x })} t="edit-cust-company" />}
                <label className="flex cursor-pointer items-center gap-2 pt-1">
                  <input type="checkbox" checked={form.vat_registered} onChange={(e) => setForm({ ...form, vat_registered: e.target.checked })} data-testid="edit-cust-vat-registered" className="h-4 w-4 rounded border-input" />
                  <span className="text-sm font-bold text-midnight">VAT registered</span>
                </label>
                {form.vat_registered && <EF label="VAT NUMBER" v={form.vat_number} on={(x) => setForm({ ...form, vat_number: x })} t="edit-cust-vat-number" />}
                <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">NOTES</span>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-cust-notes" rows={3} className="w-full rounded-lg border border-input px-3 py-2 text-sm outline-none focus:border-brand" /></label>
                <div className="flex gap-2 pt-1">
                  <button onClick={saveEdit} disabled={saving} data-testid="save-customer-btn" className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand py-2.5 text-sm font-extrabold text-white disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save</button>
                  <button onClick={() => setEditing(false)} data-testid="cancel-customer-btn" className="flex items-center justify-center gap-1 rounded-lg border border-border px-4 py-2.5 text-sm font-bold text-midnight"><X className="h-4 w-4" />Cancel</button>
                </div>
              </div>
            </>
          ) : (
            <>
              <h3 className="mb-3 font-display text-lg font-bold uppercase text-midnight">Contact</h3>
              <div className="space-y-2 text-sm text-slate-700">
                {(customer.customer_type === "company" || customer.company_name) && <p className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><span className="font-semibold text-midnight">{customer.company_name || "Company"}</span></p>}
                {customer.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" />{customer.phone}</p>}
                {customer.email && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" />{customer.email}</p>}
                {customer.address && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" />{customer.address}</p>}
                {customer.vat_registered && customer.vat_number && <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-muted-foreground" />VAT: {customer.vat_number}</p>}
                {customer.notes && <p className="flex items-start gap-2 rounded-lg bg-secondary p-2"><StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />{customer.notes}</p>}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 border-t border-border pt-4 text-center">
                <div><p className="font-display text-xl font-bold text-midnight">{money(totals.invoiced)}</p><p className="text-[10px] uppercase text-muted-foreground">Invoiced</p></div>
                <div><p className="font-display text-xl font-bold text-success">{money(totals.paid)}</p><p className="text-[10px] uppercase text-muted-foreground">Paid</p></div>
                <div><p className="font-display text-xl font-bold text-warning" data-testid="customer-outstanding">{money(totals.outstanding)}</p><p className="text-[10px] uppercase text-muted-foreground">Due</p></div>
              </div>
            </>
          )}
        </div>

        <div className="lg:col-span-2">
          <Tabs defaultValue="vehicles">
            <TabsList className="mb-3">
              <TabsTrigger value="vehicles" data-testid="tab-vehicles"><Car className="mr-1.5 h-4 w-4" />Vehicles ({vehicles.length})</TabsTrigger>
              <TabsTrigger value="jobs" data-testid="tab-jobs"><Hammer className="mr-1.5 h-4 w-4" />Jobs ({jobs.all.length})</TabsTrigger>
              <TabsTrigger value="invoices" data-testid="tab-invoices"><FileText className="mr-1.5 h-4 w-4" />Invoices ({invoices.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="vehicles" className="space-y-2">
              {vehicles.length === 0 ? <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No vehicles linked.</p> :
                vehicles.map((v) => (
                  <button key={v.id} onClick={() => navigate(`/vehicles/${v.id}`)} className="flex w-full items-center gap-3 rounded-lg border border-border bg-card p-3 text-left hover:border-brand">
                    <NumberPlate vrm={v.vrm} size="sm" /><span className="text-sm font-semibold text-midnight">{v.make} {v.model}</span>
                  </button>
                ))}
            </TabsContent>
            <TabsContent value="jobs" className="space-y-2">
              {jobs.all.length === 0 ? <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No jobs yet.</p> :
                jobs.all.map((j) => (
                  <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} className="flex w-full items-center gap-3 rounded-lg border border-border bg-card p-3 text-left hover:border-brand">
                    {j.vehicle_vrm && <NumberPlate vrm={j.vehicle_vrm} size="sm" />}
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-midnight">{j.title}</p><p className="text-xs text-muted-foreground">{money(j.totals?.total_gross)}</p></div>
                    <StatusChip status={j.status} />
                  </button>
                ))}
            </TabsContent>
            <TabsContent value="invoices" className="space-y-2">
              {invoices.length === 0 ? <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No invoices yet.</p> :
                invoices.map((inv) => (
                  <Link key={inv.id} to={`/invoice/${inv.id}`} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 hover:border-brand">
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-midnight">{inv.invoice_number}</p><p className="text-xs text-muted-foreground">{money(inv.total_amount)} · Due {money(inv.outstanding_amount)}</p></div>
                    <StatusChip status={inv.display_status || (inv.outstanding_amount <= 0 ? "paid" : inv.paid_amount > 0 ? "partial" : "unpaid")} />
                  </Link>
                ))}
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <RelatedExpenses customerId={id} />
      <WhatsAppLog customerId={customer.id} refreshKey={waRefresh} />
      <WhatsAppSendDialog
        open={waOpen} onClose={() => setWaOpen(false)} onSent={() => setWaRefresh((x) => x + 1)}
        customerId={customer.id} customerName={customer.name} to={customer.phone}
        defaultText={waCustomerText(customer, data.garage?.name)}
        kind="customer_message" entityType="customer" entityId={customer.id}
      />
      <LogCallModal
        open={callOpen} onClose={() => setCallOpen(false)}
        customerId={customer.id} customerName={customer.name}
        vehicleId={(vehicles && vehicles[0]?.id) || null} vrm={vehicles && vehicles[0]?.vrm}
      />
    </div>
  );
}

function EF({ label, v, on, t }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">{label}</span>
      <input data-testid={t} value={v} onChange={(e) => on(e.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm outline-none focus:border-brand" />
    </label>
  );
}
